import VueRouter, { Route } from 'vue-router';
import { RouteName } from '../../../routes/routes.constants';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { magic } from '@gig/magic-sdk';
import { Jurisdiction } from '@gig/magic-jurisdictions';
import {
    allowPendingVerification,
    allowPendingVerificationByConditions,
} from '../helpers';
import { getUserJurisdiction } from '../helpers/jurisdiction.helpers';
import {
    ACCOUNT_REQUIRES_VERIFICATION_QUERY,
    AML_STATUS_QUERY,
    Feature,
    IP_COUNTRY_QUERY,
} from '@gig/magic-messages';
import type { IGetAmlStatusResponse } from '@gig/magic-sdk/dist/adapter/users/getAmlStatus';
import { AmlStatus } from '@gig/magic-sdk/dist/adapter/users/getAmlStatus';
import { getFeatureEnabled } from '@gig/magic-common';
import type { IScanVerificationResponse } from '@gig/magic-sdk/dist/adapter/users/scanVerification';

/**
 * Inits the verification guard that disallows access for unverified users.
 */
export function initVerificationGuard(router: VueRouter) {
    router.beforeEach(async (to, from, next) => {
        const requiresVerification = await window.queryBus.query<boolean>(
            ACCOUNT_REQUIRES_VERIFICATION_QUERY,
        );
        if (!requiresVerification) {
            next();
            return;
        }

        if (isModalVerification(from, to)) {
            next();
            return;
        }

        if (shouldRedirectForLotba(to)) {
            next({
                name: RouteName.USER_VERIFICATION,
                params: {
                    language: to.params.language,
                },
                replace: true,
            });
            return;
        }

        const verificationRoute = await getVerificationRoute(to);
        if (
            isHomePageVerification(from, to) ||
            (await isBlockedByVerification(to)) ||
            isIdentityVerification(to, verificationRoute)
        ) {
            if (
                !isFirstNavigation(from) &&
                verificationRoute === RouteName.PENDING_VERIFICATION
            ) {
                // for ONJN jurisdiction, we allow the users to move freely in the app
                // we just redirect them in the verification page for the first time
                return next();
            }

            if (verificationRoute !== to.name) {
                return next({
                    name: verificationRoute,
                    params: {
                        language: to.params.language,
                    },
                });
            }
        }

        next();
    });
}

function isHomePageVerification(from: Route, to: Route): boolean {
    return isFirstNavigation(from) || to.name === RouteName.HOME;
}

function isFirstNavigation(from: Route): boolean {
    return !from.name;
}

function isIdentityVerification(
    to: Route,
    verificationRoute: RouteName,
): boolean {
    // if the Identity Verification feature is enabled, we always want to redirect there
    // even if the `to` page is another verification page
    const verificationRoutes = [
        RouteName.VERIFICATION,
        RouteName.USER_VERIFICATION,
        RouteName.PENDING_VERIFICATION,
    ];
    return (
        verificationRoutes.includes(to.name as RouteName) &&
        verificationRoute === RouteName.IDENTITY_VERIFICATION
    );
}

function isModalVerification(from: Route, to: Route) {
    // at this stage, the user is being redirected to a verification modal
    // eg from=/rewards, to=/rewards?modal=verification-modal
    // in this case, the guard shouldn't redirect again
    return (
        (from.name === to.name || (!from.name && to.name === RouteName.HOME)) &&
        to.query.modal
    );
}

function shouldRedirectForLotba(to: Route) {
    const shouldGoToGenericVerify =
        to.name !== RouteName.USER_VERIFICATION &&
        to.name !== RouteName.LIVE_CHAT;
    const user = magic.session.getUser();
    const isLotba = user?.country === Country.ARGENTINA;
    const hasModal = to.query && to.query.modal;

    return isLotba && shouldGoToGenericVerify && !hasModal;
}

async function getVerificationRoute(to: Route) {
    if (await isScanVerificationEnabled()) {
        // it's important not to call the `getKycVerificationUrl` api
        // when we redirect to the complete page, since this call would
        // restart the verification process
        if (to.name === RouteName.IDENTITY_VERIFICATION_COMPLETE) {
            return to.name;
        } else if (await shouldLaunchIdentityVerification()) {
            return RouteName.IDENTITY_VERIFICATION;
        }
        // else, continue to normal verification flow
    }

    const userJurisdiction = await getUserJurisdiction();
    const ipCountry = await window.queryBus.query<Country>(IP_COUNTRY_QUERY)!;

    if (userJurisdiction === Jurisdiction.ONJN) {
        return RouteName.PENDING_VERIFICATION;
    } else {
        return getVerificationRouteByCountry(ipCountry);
    }
}

function isScanVerificationEnabled(): Promise<boolean> {
    return getFeatureEnabled(Feature.SCAN_VERIFY_FLOW);
}

async function shouldLaunchIdentityVerification() {
    if (await isPhotoIdStatusComplete()) {
        return false;
    }

    return isScanVerificationUrlConfigured();
}

export async function isPhotoIdStatusComplete() {
    const aml = await window.queryBus.query<IGetAmlStatusResponse>(
        AML_STATUS_QUERY,
    );

    return (
        aml.status.photoID === AmlStatus.REJECTED ||
        aml.status.photoID === AmlStatus.APPROVED
    );
}

async function isScanVerificationUrlConfigured() {
    try {
        const response = (await magic.invokeCached(
            60,
            magic.methods.users,
            'getKycVerificationUrl',
        )()) as IScanVerificationResponse;

        return !!response?.VerificationUrl;
    } catch (e) {
        console.error('Failed to get KYC Verification URL:', e);
        return false;
    }
}

function getVerificationRouteByCountry(country?: Country) {
    switch (country) {
        case Country.NETHERLANDS:
            return RouteName.USER_VERIFICATION;
        default:
            return RouteName.VERIFICATION;
    }
}

export async function isBlockedByVerification(route: Route) {
    const matchAllow = route.matched.map(async ({ meta }) => {
        if (isVerificationStatic(meta)) {
            return (meta as ReturnType<typeof allowPendingVerification>)
                .pendingVerificationAllowed;
        }

        // NOTE: these variables indicate that the access to a page is based on user jurisdiction or ip country
        // This metadata can't be computed in the routes, since they are computed only once (when the app loads)
        // and could be invalid after a different logging out/in without refreshing
        // So, we compute them just before accessing the page, to ensure we have the latest user state
        if (isVerificationDependingOnUser(meta)) {
            const metaForUser = await allowPendingVerificationByConditions(
                meta,
            );

            return (
                metaForUser.pendingVerificationAllowed ||
                metaForUser.pathsAllowedPendingVerification?.includes(
                    metaForUser.extractPathFromRoute!(route),
                )
            );
        }

        return false;
    });

    return !(await Promise.all(matchAllow)).some((allowed) => allowed);
}

function isVerificationStatic(meta: any) {
    return meta.hasOwnProperty('pendingVerificationAllowed');
}

function isVerificationDependingOnUser(meta: any) {
    return (
        meta.hasOwnProperty('allowedPathsPerCountry') ||
        meta.hasOwnProperty('allowedPathsPerJurisdiction') ||
        meta.hasOwnProperty('allowedJurisdictions')
    );
}
